import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';

import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

import AdminShell from './components/shells/AdminShell';
import { RoleGuard } from './components/auth/RoleGuard';
import { Toaster } from './components/ui/sonner';

import Dashboard from './pages/Dashboard';
import AlertsPage from './pages/AlertsPage';

import ProductsListPage from './pages/inventory/ProductsListPage';
import ProductDetailPage from './pages/inventory/ProductDetailPage';
import ProductFormPage from './pages/inventory/ProductFormPage';
import CategoriesPage from './pages/inventory/CategoriesPage';
import SuppliersPage from './pages/inventory/SuppliersPage';
import StockLevelsPage from './pages/inventory/StockLevelsPage';
import StockAdjustmentPage from './pages/inventory/StockAdjustmentPage';
import BatchesPage from './pages/inventory/BatchesPage';
import BatchRecallPage from './pages/inventory/BatchRecallPage';
import GoodsReceiptsPage from './pages/inventory/GoodsReceiptsPage';
import GoodsReceiptFormPage from './pages/inventory/GoodsReceiptFormPage';
import GoodsReceiptDetailPage from './pages/inventory/GoodsReceiptDetailPage';
import StockTakesListPage from './pages/inventory/StockTakesListPage';
import StockTakeCreatePage from './pages/inventory/StockTakeCreatePage';
import StockTakeCountPage from './pages/inventory/StockTakeCountPage';
import StockTakeReviewPage from './pages/inventory/StockTakeReviewPage';
import StockMovementsPage from './pages/inventory/StockMovementsPage';

import LocationsDashboard from './pages/locations/LocationsDashboard';
import HubDetail from './pages/locations/HubDetail';
import LocationFormPage from './pages/locations/LocationFormPage';
import SpokeDetail from './pages/locations/SpokeDetail';
import TransfersList from './pages/locations/TransfersList';
import TransferFormPage from './pages/locations/TransferFormPage';
import TransferDetailPage from './pages/locations/TransferDetailPage';
import VenuesList from './pages/locations/VenuesList';
import VenueDetailPage from './pages/locations/VenueDetailPage';

import MachinesList from './pages/machines/MachinesList';
import MachineDetail from './pages/machines/MachineDetail';
import MachineFormPage from './pages/machines/MachineFormPage';
import MachinePicklist from './pages/machines/MachinePicklist';
import RefillSession from './pages/machines/RefillSession';
import RefillsList from './pages/machines/RefillsList';

import PlanogramsListPage from './pages/planograms/PlanogramsListPage';
import PlanogramFormPage from './pages/planograms/PlanogramFormPage';
import PlanogramBuilderPage from './pages/planograms/PlanogramBuilderPage';

import FinanceDashboard from './pages/finance/FinanceDashboard';
import HubPnL from './pages/finance/HubPnL';
import VenueCommissions from './pages/finance/VenueCommissions';

import ReportsDashboard from './pages/reports/ReportsDashboard';
import OOSReport from './pages/reports/OOSReport';
import MarginAnalysis from './pages/reports/MarginAnalysis';
import DowntimeReport from './pages/reports/DowntimeReport';
import StockValueReport from './pages/reports/StockValueReport';
import DaysOfCoverReport from './pages/reports/DaysOfCoverReport';
import SalesReport from './pages/reports/SalesReport';

import UsersSettings from './pages/settings/UsersSettings';
import RolesList from './pages/settings/RolesList';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in Route element
import RoleDetail from './pages/settings/RoleDetail';
import OrganizationSettings from './pages/settings/OrganizationSettings';
import IntegrationsBrightBlue from './pages/settings/IntegrationsBrightBlue';
import ProfilePage from './pages/settings/ProfilePage';

import PlatformDashboard from './pages/platform/PlatformDashboard';
import TenantsList from './pages/platform/TenantsList';
import TenantCreate from './pages/platform/TenantCreate';
import TenantDetail from './pages/platform/TenantDetail';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AuthOrLogin() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<AuthOrLogin />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/reset" element={<ResetPassword />} />

      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AdminShell>
              <Routes>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="alerts" element={<AlertsPage />} />

                <Route path="products" element={<ProductsListPage />} />
                <Route path="products/new" element={<ProductFormPage />} />
                <Route path="products/:id" element={<ProductDetailPage />} />
                <Route path="products/:id/edit" element={<ProductFormPage />} />
                {/* Inventory pages navigate with /inventory/products — keep aliases in sync */}
                <Route path="inventory/products" element={<ProductsListPage />} />
                <Route path="inventory/products/new" element={<ProductFormPage />} />
                <Route path="inventory/products/:id/edit" element={<ProductFormPage />} />
                <Route path="inventory/products/:id" element={<ProductDetailPage />} />
                <Route path="inventory/suppliers/:id" element={<Navigate to="/suppliers" replace />} />
                <Route path="categories" element={<CategoriesPage />} />
                <Route path="suppliers" element={<SuppliersPage />} />
                <Route path="stock" element={<StockLevelsPage />} />
                <Route path="inventory/adjustments/new" element={<StockAdjustmentPage />} />
                <Route path="batches" element={<BatchesPage />} />
                <Route path="inventory/batch-recall" element={<BatchRecallPage />} />
                <Route path="goods-receipts" element={<GoodsReceiptsPage />} />
                <Route path="inventory/goods-receipts/new" element={<GoodsReceiptFormPage />} />
                <Route path="inventory/goods-receipts/:id" element={<GoodsReceiptDetailPage />} />
                <Route path="inventory/stock-takes" element={<StockTakesListPage />} />
                <Route path="inventory/stock-takes/new" element={<StockTakeCreatePage />} />
                <Route path="inventory/stock-takes/:id/count" element={<StockTakeCountPage />} />
                <Route path="inventory/stock-takes/:id/review" element={<StockTakeReviewPage />} />
                <Route path="inventory/movements" element={<StockMovementsPage />} />

                <Route path="locations" element={<LocationsDashboard />} />
                <Route path="locations/new" element={<LocationFormPage />} />
                <Route path="locations/hubs/:id" element={<HubDetail />} />
                <Route path="locations/hubs/:id/edit" element={<LocationFormPage />} />
                <Route path="locations/spokes/:id" element={<SpokeDetail />} />
                <Route path="locations/spokes/:id/edit" element={<LocationFormPage />} />
                <Route path="transfers" element={<TransfersList />} />
                <Route path="locations/transfers" element={<TransfersList />} />
                <Route path="locations/transfers/new" element={<TransferFormPage />} />
                <Route path="locations/transfers/:id" element={<TransferDetailPage />} />
                <Route path="venues" element={<VenuesList />} />
                <Route path="locations/venues" element={<VenuesList />} />
                <Route path="locations/venues/new" element={<LocationFormPage />} />
                <Route path="locations/venues/:id" element={<VenueDetailPage />} />
                <Route path="locations/venues/:id/edit" element={<LocationFormPage />} />

                <Route path="machines" element={<MachinesList />} />
                <Route path="machines/new" element={<MachineFormPage />} />
                <Route path="machines/:id/edit" element={<MachineFormPage />} />
                <Route path="machines/:id/picklist" element={<MachinePicklist />} />
                <Route path="machines/:id" element={<MachineDetail />} />
                <Route path="refills" element={<RefillsList />} />
                <Route path="refill-sessions/:id" element={<RefillSession />} />

                <Route
                  path="planograms"
                  element={
                    <RoleGuard permission="planograms.read" redirectTo="/" fallback={null}>
                      <PlanogramsListPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="planograms/new"
                  element={
                    <RoleGuard permission="planograms.write" redirectTo="/" fallback={null}>
                      <PlanogramFormPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="planograms/:id"
                  element={
                    <RoleGuard permission="planograms.read" redirectTo="/" fallback={null}>
                      <PlanogramBuilderPage />
                    </RoleGuard>
                  }
                />

                <Route path="finance" element={<FinanceDashboard />} />
                <Route path="finance/hubs/:id" element={<HubPnL />} />
                <Route path="finance/commissions" element={<VenueCommissions />} />

                <Route path="reports" element={<ReportsDashboard />} />
                <Route path="reports/out-of-stock" element={<OOSReport />} />
                <Route path="reports/oos" element={<Navigate to="/reports/out-of-stock" replace />} />
                <Route path="reports/margins" element={<MarginAnalysis />} />
                <Route path="reports/margin" element={<Navigate to="/reports/margins" replace />} />
                <Route path="reports/downtime" element={<DowntimeReport />} />
                <Route path="reports/stock-value" element={<StockValueReport />} />
                <Route path="reports/days-of-cover" element={<DaysOfCoverReport />} />
                <Route path="reports/sales" element={<SalesReport />} />

                <Route
                  path="settings"
                  element={
                    <RoleGuard permission="settings.read" redirectTo="/" fallback={null}>
                      <OrganizationSettings />
                    </RoleGuard>
                  }
                />
                <Route path="settings/profile" element={<ProfilePage />} />
                <Route path="settings/users" element={<UsersSettings />} />
                <Route path="settings/roles" element={<RolesList />} />
                <Route path="settings/roles/:id" element={<RoleDetail />} />
                <Route path="settings/integrations" element={<IntegrationsBrightBlue />} />

                <Route path="platform" element={<PlatformDashboard />} />
                <Route path="platform/tenants" element={<TenantsList />} />
                <Route path="platform/tenants/new" element={<TenantCreate />} />
                <Route path="platform/tenants/:id" element={<TenantDetail />} />
              </Routes>
            </AdminShell>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <AppRoutes />
        <Toaster richColors closeButton position="top-right" />
      </TenantProvider>
    </AuthProvider>
  );
}
